import { useState } from 'react'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Flashbar from '@cloudscape-design/components/flashbar'
import Header from '@cloudscape-design/components/header'
import Modal from '@cloudscape-design/components/modal'
import SpaceBetween from '@cloudscape-design/components/space-between'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import Table from '@cloudscape-design/components/table'
import Tabs from '@cloudscape-design/components/tabs'
import type { FlashbarProps } from '@cloudscape-design/components/flashbar'
import {
  useEmailCampaign,
  useCheckCampaignReplies,
  useMarkCampaignProviderFollowup,
} from '@/hooks/useEmailCampaigns'
import type { EmailCampaign, EmailCampaignProvider } from '@/lib/types'

interface CampaignDetailViewProps {
  clientId: string
  campaign: EmailCampaign
  onBack: () => void
}

function providerStatusIndicator(status: string) {
  switch (status) {
    case 'sent':
      return <StatusIndicator type="success">Sent</StatusIndicator>
    case 'replied':
      return <StatusIndicator type="success">Replied</StatusIndicator>
    case 'failed':
      return <StatusIndicator type="error">Failed</StatusIndicator>
    case 'needs_followup':
      return <StatusIndicator type="warning">Needs Follow-up</StatusIndicator>
    case 'pending':
      return <StatusIndicator type="pending">Pending</StatusIndicator>
    default:
      return <StatusIndicator type="info">{status}</StatusIndicator>
  }
}

export default function CampaignDetailView({
  clientId,
  campaign: initialCampaign,
  onBack,
}: CampaignDetailViewProps) {
  const { campaign: liveCampaign, refetch } = useEmailCampaign(clientId, initialCampaign.id)
  const campaign = liveCampaign ?? initialCampaign

  const { checkReplies, loading: checkingReplies } = useCheckCampaignReplies()
  const { markFollowup, loading: markingFollowup } = useMarkCampaignProviderFollowup()
  const [flash, setFlash] = useState<FlashbarProps.MessageDefinition[]>([])
  const [activeTab, setActiveTab] = useState('all')
  const [replyModalProvider, setReplyModalProvider] = useState<EmailCampaignProvider | null>(null)

  const filteredProviders: EmailCampaignProvider[] =
    activeTab === 'all'
      ? campaign.providers
      : campaign.providers.filter((p) => p.status === activeTab)

  const counts = {
    all: campaign.providers.length,
    sent: campaign.providers.filter((p) => p.status === 'sent').length,
    replied: campaign.providers.filter((p) => p.status === 'replied').length,
    needs_followup: campaign.providers.filter((p) => p.status === 'needs_followup').length,
    failed: campaign.providers.filter((p) => p.status === 'failed').length,
  }

  const handleCheckReplies = async () => {
    try {
      const result = await checkReplies(clientId, campaign.id)
      await refetch()
      const newReplied = result.data?.checkCampaignReplies?.repliedCount ?? campaign.repliedCount
      const delta = newReplied - campaign.repliedCount
      setFlash([{
        type: 'success',
        content: delta > 0
          ? `Found ${delta} new ${delta === 1 ? 'reply' : 'replies'}`
          : 'No new replies found',
        dismissible: true,
        onDismiss: () => setFlash([]),
      }])
    } catch (err) {
      setFlash([{
        type: 'error',
        content: `Failed to check replies: ${err instanceof Error ? err.message : 'Unknown error'}`,
        dismissible: true,
        onDismiss: () => setFlash([]),
      }])
    }
  }

  const handleMarkFollowup = async (provider: EmailCampaignProvider, needsFollowup: boolean) => {
    try {
      await markFollowup(clientId, campaign.id, provider.providerNpi, needsFollowup)
      await refetch()
    } catch (err) {
      setFlash([{
        type: 'error',
        content: `Failed to update follow-up status: ${err instanceof Error ? err.message : 'Unknown error'}`,
        dismissible: true,
        onDismiss: () => setFlash([]),
      }])
    }
  }

  return (
    <SpaceBetween size="m">
      {flash.length > 0 && <Flashbar items={flash} />}
      <Button onClick={onBack} iconName="arrow-left">Back to Campaigns</Button>
      <SpaceBetween size="s">
        <Header
          description={`${campaign.sentCount} sent · ${campaign.repliedCount} replied · ${campaign.failedCount} failed`}
          actions={
            <Button
              loading={checkingReplies}
              onClick={() => { void handleCheckReplies() }}
            >
              {checkingReplies ? 'Checking…' : 'Check for Replies'}
            </Button>
          }
        >
          {campaign.name}
        </Header>
        <Tabs
          activeTabId={activeTab}
          onChange={({ detail }) => setActiveTab(detail.activeTabId)}
          tabs={[
            { id: 'all', label: `All (${counts.all})` },
            { id: 'sent', label: `Sent (${counts.sent})` },
            { id: 'replied', label: `Replied (${counts.replied})` },
            { id: 'needs_followup', label: `Needs Follow-up (${counts.needs_followup})` },
            { id: 'failed', label: `Failed (${counts.failed})` },
          ]}
        />
      </SpaceBetween>
      <Table
        items={filteredProviders}
        columnDefinitions={[
          {
            id: 'name',
            header: 'Provider',
            cell: (item) => item.providerName,
          },
          {
            id: 'email',
            header: 'Email',
            cell: (item) => item.email,
          },
          {
            id: 'status',
            header: 'Status',
            cell: (item) => providerStatusIndicator(item.status),
          },
          {
            id: 'sentAt',
            header: 'Sent At',
            cell: (item) =>
              item.sentAt ? new Date(item.sentAt).toLocaleString() : '—',
          },
          {
            id: 'repliedAt',
            header: 'Replied At',
            cell: (item) =>
              item.repliedAt ? new Date(item.repliedAt).toLocaleString() : '—',
          },
          {
            id: 'actions',
            header: 'Actions',
            cell: (item) => {
              if (item.status === 'replied' && item.replyBody) {
                return (
                  <Button
                    variant="inline-link"
                    onClick={() => setReplyModalProvider(item)}
                  >
                    View Reply
                  </Button>
                )
              }
              if (item.status === 'sent') {
                return (
                  <Button
                    variant="inline-link"
                    disabled={markingFollowup}
                    onClick={() => { void handleMarkFollowup(item, true) }}
                  >
                    Mark Follow-up Needed
                  </Button>
                )
              }
              if (item.status === 'needs_followup') {
                return (
                  <Button
                    variant="inline-link"
                    disabled={markingFollowup}
                    onClick={() => { void handleMarkFollowup(item, false) }}
                  >
                    Remove Follow-up
                  </Button>
                )
              }
              if (item.error) {
                return (
                  <Box variant="small" color="text-status-error">
                    {item.error}
                  </Box>
                )
              }
              return null
            },
          },
        ]}
        empty={
          <Box textAlign="center" padding="l">
            <Box variant="p" color="text-status-inactive">
              No providers match this filter.
            </Box>
          </Box>
        }
      />
      {replyModalProvider && (
        <Modal
          visible
          header={`Reply from ${replyModalProvider.providerName}`}
          onDismiss={() => setReplyModalProvider(null)}
          footer={
            <Box float="right">
              <Button variant="primary" onClick={() => setReplyModalProvider(null)}>Close</Button>
            </Box>
          }
          size="large"
        >
          <Box variant="pre">{replyModalProvider.replyBody}</Box>
        </Modal>
      )}
    </SpaceBetween>
  )
}
